import { BrandZohoTypes } from "@elit-hair/brands";

import type { ModuleType } from "@/types/zoho.types";

export enum SupportedModuleType {
    LEADS = "Leads",
    CONTACTS = "Contacts",
    // Future modules will be added here as needed
}

enum fieldNames {
    STATUS = "Status",
    SOURCE = "Source",
}

type SupportedRecord = BrandZohoTypes["Leads"] | BrandZohoTypes["Contacts"];

// Module-specific field configurations
const MODULE_FIELDS = {
    [SupportedModuleType.LEADS]: {
        phoneFields: ["Mobile", "Phone"],
        nameFields: ["Full_Name", "First_Name", "Last_Name"],
        imageFields: ["Bild_1", "Bild_2", "Bild_3", "Bild_4"],
        displayFields: "ALL", // "ALL" = show all fields, or specify array of specific fields
    },
    [SupportedModuleType.CONTACTS]: {
        phoneFields: ["Mobile", "Phone"],
        nameFields: ["Full_Name", "First_Name", "Last_Name"],
        imageFields: ["Bild_1", "Bild_2", "Bild_3", "Bild_4"],
        displayFields: "ALL", // "ALL" = show all fields, or specify array of specific fields
    },
} satisfies Record<
    SupportedModuleType,
    {
        phoneFields: readonly string[];
        nameFields: readonly string[];
        imageFields: readonly string[];
        displayFields: "ALL" | readonly string[];
    }
>;

// Default configuration for unknown module types
const DEFAULT_FIELDS = {
    phoneFields: [],
    nameFields: [],
    imageFields: [],
    displayFields: "ALL",
} satisfies {
    phoneFields: readonly string[];
    nameFields: readonly string[];
    imageFields: readonly string[];
    displayFields: "ALL" | readonly string[];
};

export function getModuleFields(moduleType: ModuleType) {
    const supportedModule = Object.values(SupportedModuleType).find((supported) => supported === moduleType);

    if (supportedModule) {
        return MODULE_FIELDS[supportedModule];
    }
    return DEFAULT_FIELDS;
}

export function getDisplayFields(record: SupportedRecord, moduleType: ModuleType): string[] {
    const fields = getModuleFields(moduleType);

    // Always include special fields (phone, name, image)
    const specialFields = [...fields.phoneFields, ...fields.nameFields, ...fields.imageFields];

    // If displayFields is "ALL", show all fields
    if (fields.displayFields === "ALL") {
        return Object.keys(record);
    }

    // Otherwise, show specified fields + special fields
    const displayFields = Array.isArray(fields.displayFields) ? fields.displayFields : [];
    return [...new Set([...displayFields, ...specialFields])];
}

export function getPhoneFieldLabel(fieldName: string): string {
    switch (fieldName) {
        case "Mobile":
            return "Mobile";
        case "Phone":
            return "Phone";
        default:
            return "Phone Number";
    }
}

export function getPhoneNumbersFromRecord(
    record: SupportedRecord,
    moduleType: ModuleType
): Array<{ field: string; number: string }> {
    const fields = getModuleFields(moduleType);
    return fields.phoneFields
        .map((field) => {
            const entry = Object.entries(record).find(([key]) => key === field);
            const [, value] = entry || [];
            return typeof value === "string" ? { field, number: value.trim() } : null;
        })
        .filter((phone): phone is { field: string; number: string } => phone !== null && phone.number.length > 0);
}

export function getPrimaryPhoneNumber(record: SupportedRecord, moduleType: ModuleType): string | null {
    const phones = getPhoneNumbersFromRecord(record, moduleType);
    return phones[0]?.number ?? null;
}

export function getDisplayName(record: SupportedRecord, moduleType: ModuleType): string {
    const fields = getModuleFields(moduleType);
    for (const field of fields.nameFields) {
        const entry = Object.entries(record).find(([key]) => key === field);
        const [, value] = entry || [];
        if (typeof value === "string" && value.trim().length > 0) {
            return value;
        }
    }
    return `ID: ${record.id}`;
}

export function getImageFields(record: SupportedRecord, moduleType: ModuleType): string[] {
    const fields = getModuleFields(moduleType);
    return fields.imageFields
        .map((field) => {
            const entry = Object.entries(record).find(([key]) => key === field);
            const [, value] = entry || [];
            return typeof value === "string" ? value : null;
        })
        .filter((img): img is string => img !== null && img.trim().length > 0)
        .map((img) => img.trim());
}

// Helper function to get field value with fallback support
function getFieldValue(record: Record<string, unknown>, primaryField: string, fallbackField?: string): unknown {
    const primaryValue = record[primaryField];
    if (primaryValue) {
        return primaryValue;
    }
    if (fallbackField) {
        return record[fallbackField];
    }
    return undefined;
}

export function getStatusAndSource(record: Record<string, unknown>, moduleType: ModuleType) {
    const normalize = (value: unknown): string | undefined => (typeof value === "string" ? value : undefined);

    // Get fallback fields for Contacts module
    const fallbackStatusField = moduleType === "Contacts" ? fieldNames.STATUS : undefined;
    const fallbackSourceField = moduleType === "Contacts" ? fieldNames.SOURCE : undefined;

    // Get status with fallback support
    const statusValue = getFieldValue(record, fieldNames.STATUS, fallbackStatusField);

    // Get source with fallback support
    const sourceValue = getFieldValue(record, fieldNames.SOURCE, fallbackSourceField);

    return {
        status: normalize(statusValue),
        source: normalize(sourceValue),
    };
}
